// Identidad compartida Solar Hub ⇄ FluxAI — suite de Alebas Ingeniería.
// ─────────────────────────────────────────────────────────────────────────────
// ESPEJO ESTRUCTURAL del contrato de FluxAI (`web/src/lib/auth.ts`): mismos claims,
// mismos roles y el MISMO mapeo de audiencia, para que un usuario (y su rol) encaje
// en las DOS apps sin reescribir la identidad. El JWT lo emite el plano de gestión
// de FluxAI (api/ Laravel) como AUTORIDAD ÚNICA; acá solo leemos sus claims (sin
// validar firma — eso lo hace el backend que entrega datos).
//
//   { role:"administrator", tenant_id }              → tenant / OR        (operator)
//   { role:"administrator", tenant_id, user_id }     → cliente final      (user)
//   { role:"technician" }                            → técnico de campo   (technician)
//   { role:"admin" | "super_administrator" | "support" } → interno FluxAI (admin)
//
// Mientras api/ (login real / SSO) no esté desplegado, Solar Hub sigue usando su
// login legacy de admin (App.jsx); este módulo deja la estructura LISTA para el SSO
// y habilita el gateo por rol/entitlement (registro-para-cotizar premium) sin romper
// el flujo gratuito actual (default-allow hasta que haya login real).

// Token de la suite (mismo nombre lógico que FluxAI). En dominios distintos el
// localStorage NO se comparte; el SSO real llega con el handoff de api/ (capa FL-4).
import { serviciosLocales } from './entitlements';
import { loginAdminRemoto, loginAdminGoogleRemoto, sesionClienteGoogleRemoto, verificarSegundoFactorRemoto, cerrarAdmin } from './adminAuth';

const KEY = 'fluxai.jwt';

// ── Roles canónicos (idénticos a UserRole de FluxAI) ────────────────────────
export const ROLES = Object.freeze({
  ROOT: 'super_administrator',
  SUPPORT: 'support',
  ADMIN: 'administrator', // cuenta tenant
  USUARIO: 'client',      // cliente final
  TECNICO: 'technician',
  // Rol de MARKETPLACE, propio de Solar Hub (FluxAI no expone consola al proveedor).
  // Participa en el comercio (inventario/PO/comisiones) pero su audiencia es 'supplier',
  // no una audiencia de la consola FluxAI. Cierra la brecha detectada en la auditoría.
  PROVEEDOR: 'supplier',
});

// Tipos de instalador de Solar Hub (RETIE) → rol compartido. Técnico (CONTE),
// ingeniero (COPNIA) y EMPRESA instaladora entran como `technician`:
// una empresa también instala, no solo la persona natural.
// La distinción vive en `installer_type`.
export function rolDesdeInstalador(installerType) {
  return ROLES.TECNICO;
}

/** Cómo se inscribe alguien en Solar Hub (captación). */
export const PERFILES_ALTA = Object.freeze({
  CLIENTE: 'cliente',
  INSTALADOR: 'instalador',
  PROVEEDOR: 'proveedor',
});

/** Persona, profesional o empresa que instala. */
export const TIPOS_INSTALADOR = Object.freeze({
  TECNICO: 'tecnico',
  INGENIERO: 'ingeniero',
  EMPRESA: 'empresa',
});

export function rolDesdePerfil(perfil) {
  if (perfil === PERFILES_ALTA.PROVEEDOR) return ROLES.PROVEEDOR;
  if (perfil === PERFILES_ALTA.INSTALADOR) return ROLES.TECNICO;
  return ROLES.USUARIO;
}

export function destinoTrasAlta(perfil) {
  if (perfil === PERFILES_ALTA.PROVEEDOR) return 'proveedor';
  if (perfil === PERFILES_ALTA.INSTALADOR) return 'instalador';
  return 'home';
}

// 🔴 LIMPIEZA DE UN RESIDUO CON DATOS PERSONALES DE TERCEROS — `sh:leads`.
//
// `registrarBasico` guardaba `{nombre, email}` de cada persona que se registraba, hasta 100, en el
// localStorage del dispositivo, «para el alta real posterior». Esa alta posterior **no existía**:
// se buscó en todo el repo y NADIE leía esa llave — era una escritura sin lector, o sea recolección
// sin finalidad (Ley 1581/2012), que es justo lo que la política de retención vino a cerrar.
//
// Y no eran datos del dueño del dispositivo: eran de **las personas anteriores**. En un navegador
// compartido —un stand de feria, el computador de la oficina— quedaba la lista de nombres y correos
// de los 100 registros previos, legible desde las herramientas del navegador, sin caducidad y sin
// borrarse al cerrar sesión.
//
// Se borra al cargar el módulo y no en el logout a propósito: un dispositivo donde nadie vuelva a
// iniciar sesión nunca ejecutaría el logout, y ahí es justamente donde el residuo se quedaría para
// siempre. `purgarResiduosLocales` se exporta para poder probarlo.
export function purgarResiduosLocales() {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem('sh:leads'); } catch { /* modo privado */ }
}
purgarResiduosLocales();

export function getToken() {
  if (typeof window === 'undefined') return null;
  try { return window.localStorage.getItem(KEY); } catch { return null; }
}

export function setToken(token) {
  if (typeof window === 'undefined') return;
  try {
    if (token) window.localStorage.setItem(KEY, token);
    else window.localStorage.removeItem(KEY);
  } catch { /* almacenamiento no disponible */ }
  try { window.dispatchEvent(new Event('solarhub-auth')); } catch {}
}

// Decodifica el payload del JWT (base64url) sin validar firma — igual que FluxAI.
export function decodeClaims(token) {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

export function currentClaims() {
  return decodeClaims(getToken());
}

// ¿El token está FIRMADO por un backend real? (el demo de arranque es alg:none.)
export function isSignedToken(token) {
  if (!token) return false;
  const parts = token.split('.');
  if (parts.length < 3 || !parts[2]) return false;
  try {
    const h = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
    return !!h.alg && h.alg !== 'none';
  } catch {
    return false;
  }
}

/**
 * Audiencia de la sesión — MISMO mapeo que FluxAI (`auth.ts::audience`):
 *   root / support / admin            → "admin"      (internos; ven todo)
 *   administrator (tenant)            → "operator"   (su tenant)
 *   client (usuario) | user_id        → "user"       (solo lo suyo)
 *   technician                        → "technician" (operación de campo)
 */
export function audience(c) {
  if (!c) return null;
  const r = String(c.role ?? '');
  const type = String(c.type ?? '');
  const tech = r === 'technician' || r === 'tecnico' || type === 'technician';
  const client = r === 'client' || r === 'usuario' || type === 'client';
  const tenant = c.tenant_id != null && String(c.tenant_id) !== '';
  if (r === 'supplier' || type === 'supplier') return 'supplier';
  // Con tenant NUNCA es root (lección FluxAI 2026-08-15): CEELVA con role=admin
  // no puede ver el catálogo entero ni concatenar la suite AMI aquí.
  if (tenant) {
    if (tech) return 'technician';
    if (client) return 'user';
    return 'operator';
  }
  if (tech) return 'technician';
  if (client || c.user_id != null) return 'user';
  if (r === 'admin' || r === 'super_administrator' || r === 'support'
      || type === 'super_administrator' || type === 'support') return 'admin';
  if (r === 'administrator' || type === 'administrator') return 'operator';
  return 'user';
}

/** ¿El usuario es un proveedor del marketplace? */
export function isProveedor(c = currentClaims()) {
  return audience(c) === 'supplier' || String(c?.role ?? '') === ROLES.PROVEEDOR;
}

/** ¿El usuario tiene rol de campo (técnico/ingeniero RETIE)? */
export function isTecnico(c = currentClaims()) {
  return audience(c) === 'technician' || String(c?.role ?? '') === ROLES.TECNICO;
}

/** Interno de FluxAI (root/soporte/admin de plataforma). */
export function isAdmin(c = currentClaims()) {
  return audience(c) === 'admin';
}

/** ¿El token venció? (claim `exp`, epoch segundos). */
export function isExpired(c) {
  return !!c?.exp && c.exp * 1000 < Date.now();
}

/** Hay sesión válida (token presente y no vencido). */
export function isAuthenticated() {
  const c = currentClaims();
  return !!c && !isExpired(c);
}

/**
 * ¿Puede consumir SERVICIOS PREMIUM de Solar Hub (estudio/dimensionamiento de alto
 * nivel)? Por ROL (técnico/ingeniero, tenant-admin o interno) O por ENTITLEMENT
 * explícito en el claim (`servicios`/`entitlements` que incluya 'solar_study').
 * Default-allow MIENTRAS no haya login real: sin sesión devuelve `true` para no
 * romper el flujo gratuito actual; el gateo duro se activa al desplegar api/.
 */
export function puedePremium(c = currentClaims()) {
  if (!c) return true; // sin login real todavía → no bloquear (flujo legacy)
  if (isTecnico(c) || audience(c) === 'admin' || audience(c) === 'operator') return true;
  // incluye los entitlements LOCALES de prueba (serviciosContratados los mezcla)
  return serviciosContratados(c).some((s) => s.includes('solar'));
}

/**
 * ¿Tiene MONITOREO de FluxAI activo (equipos en telemetría)? Habilita las features que
 * exigen DATO REAL MEDIDO (M&V medido-vs-simulado) — p. ej. el **informe ISO 50001 COMPLETO**.
 * Lo marca el entitlement `monitoreo_premium`/`monitoreo` o un claim de equipos
 * (`flux_devices`/`devices` > 0) que emitirá el login real de la suite (api/ de FluxAI).
 * Sin sesión → false: la feature COMPLETA exige telemetría; la versión ESTIMADA queda abierta.
 */
export function tieneMonitoreoFluxAI(c = currentClaims()) {
  if (!c) return false;
  // El ADMIN/dueño (root/soporte) tiene la flota REAL de FluxAI → monitoreo siempre activo
  // (sus equipos en telemetría habilitan las features M&V medido-vs-simulado).
  if (audience(c) === 'admin') return true;
  if (Number(c.flux_devices ?? c.devices ?? 0) > 0) return true;
  return serviciosContratados(c).some((s) => s.includes('monitoreo'));
}

/** ¿Puede usar el informe ISO 50001 COMPLETO (requiere telemetría FluxAI)? */
export function puedeIsoCompleto(c = currentClaims()) {
  return tieneMonitoreoFluxAI(c);
}

/**
 * Servicios CONTRATADOS por el usuario en TODA la suite (una sola base). Lee el claim
 * `servicios`/`entitlements`/`services` del JWT que emite el `api/` de FluxAI (autoridad única):
 * incluye lo contratado en Solar Hub Y en FluxAI. El login en cualquiera de las dos apps trae
 * la MISMA lista → un solo soporte, una sola fuente de verdad.
 */
export function serviciosContratados(c = currentClaims()) {
  const ents = c?.servicios || c?.entitlements || c?.services || [];
  const jwt = Array.isArray(ents) ? ents.map((s) => String(s)) : [];
  // MEZCLA con los entitlements LOCALES asignados por el admin (capa de pruebas
  // mientras no esté la compra ni el api/). Indexados por el email de la sesión.
  let local = [];
  try { local = c?.email ? serviciosLocales(c.email) : []; } catch { /* sin store */ }
  return Array.from(new Set([...jwt, ...local]));
}

/** ¿El usuario tiene CONTRATADO el servicio (por SKU), en cualquiera de las dos plataformas? */
export function tieneServicio(sku, c = currentClaims()) {
  return serviciosContratados(c).includes(String(sku));
}

/** Etiqueta de perfil para la UI (nombre + rol), según los 5 roles compartidos. */
export function profileLabel(c = currentClaims()) {
  if (!c) return 'Invitado';
  const a = audience(c);
  const nombre = (typeof c.name === 'string' && c.name) || (typeof c.email === 'string' && c.email) || '';
  const rol =
    a === 'admin' ? (c.role === 'support' ? 'Soporte' : 'Admin')
    : a === 'supplier' ? 'Proveedor'
    : a === 'technician' ? 'Técnico'
    : a === 'operator' ? 'Operador'
    : 'Cliente';
  return nombre ? `${nombre} · ${rol}` : rol;
}

// ── Sesión / credenciales UNIFICADAS de la suite Alebas ───────────────────────────────────────────
// La AUTORIDAD ÚNICA es el `api/` de FluxAI: las MISMAS credenciales sirven en Solar Hub, FluxAI y la
// app móvil. Mientras `api/` no esté desplegado, `login()` cae a un registro básico client-side
// (sesión de rol `client`) para no bloquear la cotización; al desplegar `api/` se emite el JWT firmado.

const b64url = (obj) => btoa(unescape(encodeURIComponent(JSON.stringify(obj))))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/** Acuña un JWT SIN firma (alg:none) con los claims dados (sesión de arranque, pre-`api/`). */
export function mintToken(claims, ttlSeconds = 60 * 60 * 12) {
  const header = b64url({ alg: 'none', typ: 'JWT' });
  const payload = b64url({ ...claims, exp: Math.floor(Date.now() / 1000) + ttlSeconds });
  return `${header}.${payload}.`;
}

/** Cierra sesión. */
export function logout() { setToken(null); cerrarAdmin(); }

/**
 * Puente SSO ENTRANTE: si la URL trae `?sso=<jwt>` (de FluxAI o la app móvil), abre sesión con él
 * y limpia el parámetro. Una sola credencial en toda la suite. Devuelve true si ingirió un token.
 */
export function ingestSsoToken() {
  if (typeof window === 'undefined') return false;
  try {
    const url = new URL(window.location.href);
    const sso = url.searchParams.get('sso');
    if (!sso) return false;
    url.searchParams.delete('sso');
    window.history.replaceState({}, '', url.toString());
    setToken(sso);
    return true;
  } catch { return false; }
}

const SUITE_AUTH_URL = (process.env.REACT_APP_SUITE_AUTH_URL || '').replace(/\/+$/, '');

/**
 * Login con CREDENCIALES UNIFICADAS de la suite. Si hay autoridad (`api/` de FluxAI vía
 * `REACT_APP_SUITE_AUTH_URL`), valida y abre sesión con el JWT firmado (claims + `servicios[]`
 * contratados en AMBAS plataformas). Si no hay autoridad o falla, lanza Error (la UI ofrece el
 * registro básico para cotizar). NO inventa credenciales: la verificación real la hace `api/`.
 */
// Credenciales de admin UNIFICADAS con FluxAI (las MISMAS en las dos apps): el dueño usa
// una sola cuenta para Solar Hub y FluxAI. Espejo de FluxAI `config.ts` demoAdmin*.
// (Interino hasta `api/`; overridable por env.)
const DEMO_ADMIN_EMAIL = (process.env.REACT_APP_ADMIN_EMAIL || 'ing@alebas.co').toLowerCase();
const DEMO_ADMIN_PASS = process.env.REACT_APP_ADMIN_PASS || 'FluxAI2026*';
const DEMO_ADMIN_NAME = process.env.REACT_APP_ADMIN_NAME || 'Alebas Ingeniería';

// SOPORTE de la suite Alebas (interno, compartido en las DOS plataformas): support@fluxai.solutions
// (Google Workspace). Entra como rol SOPORTE (audiencia 'admin' interna) → tramita órdenes/PQR/
// soporte. Cuenta de Workspace → el camino normal es login con Google; el password es opt-in por env.
const SUPPORT_EMAIL = (process.env.REACT_APP_SUPPORT_EMAIL || 'support@fluxai.solutions').toLowerCase();
const SUPPORT_PASS = process.env.REACT_APP_SUPPORT_PASS || ''; // sin default: soporte entra por Google
const SUPPORT_NAME = process.env.REACT_APP_SUPPORT_NAME || 'Soporte Alebas';

export async function login(email, password) {
  const mail = String(email || '').trim();
  if (!mail || !password) throw new Error('Ingresa correo y contraseña.');

  // ── ADMINISTRACIÓN: el backend propio manda si está en servicio (F9) ────────────────────────
  // 🔴 Por qué esto va PRIMERO y por qué apaga el atajo de abajo: `DEMO_ADMIN_PASS` sale de
  // `REACT_APP_ADMIN_PASS`, y las `REACT_APP_*` se hornean en el bundle → la clave del admin es
  // PÚBLICA. Mientras no exista otro login eso es lo único que hay; en cuanto existe uno real, el
  // atajo tiene que morir, o la clave vieja seguiría abriendo la misma puerta.
  //
  // `disponible:false` = no hay admin provisionado (o no hay backend) → todo sigue como antes.
  const admin = await loginAdminRemoto(mail, password);
  if (admin.disponible) {
    if (admin.ok && admin.requiere_2fa) {
      // Clave correcta, pero la cuenta exige segundo factor: el backend ya mandó el código por
      // WhatsApp y NO hay token todavía. Se avisa a la UI con un error TIPADO (no es un fallo: es un
      // paso más) para que muestre el campo del código y llame `completarLogin2FA`.
      throw Object.assign(new Error('Te enviamos un código por WhatsApp para confirmar tu identidad.'), {
        requiere_2fa: true, email: mail, pista: admin.pista,
      });
    }
    if (admin.ok && admin.token) {
      // Token FIRMADO por el backend → `isSignedToken` pasa a ser cierto y el gateo por rol
      // (`navAllowed`) empieza a aplicar de verdad, no en modo default-allow.
      setToken(admin.token);
      return currentClaims();
    }
    throw new Error(admin.error || 'Credenciales incorrectas.');
  }

  // Acceso ADMIN del dueño (rol diferencial) — interino hasta `api/`. Abre sesión de rol admin
  // → la consola muestra el backoffice por ROL (no por pestaña pública). Reemplaza al login
  // legacy `btoa`. Con `api/` desplegado, el admin real llega por el login unificado de abajo.
  if (mail.toLowerCase() === DEMO_ADMIN_EMAIL && password === DEMO_ADMIN_PASS) {
    setToken(mintToken({ role: ROLES.ROOT, email: mail, name: DEMO_ADMIN_NAME, admin: true }));
    return currentClaims();
  }
  // SOPORTE por correo/clave (solo si se configuró REACT_APP_SUPPORT_PASS; si no, soporte entra por Google).
  if (SUPPORT_PASS && mail.toLowerCase() === SUPPORT_EMAIL && password === SUPPORT_PASS) {
    setToken(mintToken({ role: ROLES.SUPPORT, email: mail, name: SUPPORT_NAME }));
    return currentClaims();
  }

  const local = await entrarCuentaLocal(mail, password);
  if (local) {
    setToken(mintToken({
      role: local.role || ROLES.USUARIO,
      email: local.email,
      name: local.name,
      basico: true,
      perfil: local.perfil || PERFILES_ALTA.CLIENTE,
      installer_type: local.installerKind || undefined,
      empresa: local.empresa || undefined,
    }));
    return currentClaims();
  }

  if (!SUITE_AUTH_URL) throw new Error('Correo o contraseña incorrectos. Si te registraste, usa la misma clave o entra con Google.');
  const res = await fetch(`${SUITE_AUTH_URL}/auth/login`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email: mail, password }),
  });
  if (res.status === 401 || res.status === 422) throw new Error('Credenciales incorrectas.');
  if (!res.ok) throw new Error('No se pudo iniciar sesión. Intenta más tarde.');
  const j = await res.json();
  const token = j.access_token || j.access || j.jwt;
  if (!token) throw new Error('Respuesta de login inválida.');
  setToken(token);
  return currentClaims();
}

/**
 * Segundo paso del login de administración con 2FA: manda el código OTP que llegó por WhatsApp. Con
 * el código válido el backend emite el token firmado y se abre la sesión, igual que un login normal.
 * Se llama SOLO después de que `login()` lanzó el error tipado `requiere_2fa`.
 */
export async function completarLogin2FA(email, code) {
  const r = await verificarSegundoFactorRemoto(String(email || '').toLowerCase().trim(), String(code || '').trim());
  if (r.disponible && r.ok && r.token) {
    setToken(r.token);
    return currentClaims();
  }
  throw new Error(r.error || 'Código incorrecto.');
}

/**
 * Login con GOOGLE (Identity Services) — credenciales unificadas de la suite. `credential` es el
 * ID token (JWT firmado por Google) del botón. El dueño (DEMO_ADMIN_EMAIL) entra como admin; otros
 * abren sesión de cliente para cotizar (el rol/servicios reales los fija `api/` al desplegarse).
 * Mismo patrón que FluxAI `auth.ts::loginWithGoogle`.
 */
export async function loginWithGoogle(credential) {
  const g = decodeClaims(credential);
  const email = String(g?.email || '').trim().toLowerCase();
  if (!email) throw new Error('No se pudo leer la cuenta de Google.');

  // 🔑 «UNA SOLA CLAVE PARA TODA LA SUITE» (dueño, 2026-09-06). ANTES de cualquier atajo local, la
  // cuenta de Google se le presenta al backend propio, que la verifica contra el Client ID de la
  // suite y emite el MISMO token firmado que `/admin-login`. Va primero por lo mismo que en
  // `login()`: en cuanto hay autoridad, el atajo sin firma no puede seguir corriendo.
  //
  // 🔴 Medido el 6-sep-2026: el root entraba con Google, el atajo de abajo le acuñaba una sesión
  // `alg:none` con rol root, y `exigeAdmin` la rechazaba — el dueño «entraba» y cada pantalla de
  // administración respondía 401. La sesión existía; la credencial no.
  const admin = await loginAdminGoogleRemoto(credential);
  if (admin.disponible) {
    if (admin.ok && admin.requiere_2fa) {
      throw Object.assign(new Error('Te enviamos un código por WhatsApp para confirmar tu identidad.'), {
        requiere_2fa: true, email, pista: admin.pista,
      });
    }
    if (admin.ok && admin.token) {
      setToken(admin.token);
      return currentClaims();
    }
    // El backend contestó y dijo que NO. Para el dueño eso es autoritativo: no se le fabrica una
    // sesión de root sin firma que después no abre nada. Para cualquier otra cuenta, «no es admin»
    // es lo esperado, y sigue el camino de cliente de siempre.
    if (email === DEMO_ADMIN_EMAIL) throw new Error(admin.error || 'Esta cuenta de Google no es administradora.');
  }

  // Sin backend (o sin admin provisionado): los atajos locales de siempre.
  if (email === DEMO_ADMIN_EMAIL) {
    setToken(mintToken({ role: ROLES.ROOT, email, name: g?.name || DEMO_ADMIN_NAME, admin: true }));
    return currentClaims();
  }
  // SOPORTE de la suite: support@fluxai.solutions entra como rol Soporte (interno) en ambas apps.
  if (email === SUPPORT_EMAIL) {
    setToken(mintToken({ role: ROLES.SUPPORT, email, name: g?.name || SUPPORT_NAME }));
    return currentClaims();
  }
  // Sesión FIRMADA de cliente (llave propia de Solar Hub, rol `client`): con ella el servidor puede
  // verificar quién es, que es lo que exige el registro RETIE. Si no está en servicio, sigue lo de
  // siempre. Una respuesta negativa de Google tampoco corta: se cae a la sesión local para cotizar.
  const propia = (await sesionClienteGoogleRemoto(credential, g?.name)) || {};
  if (propia.ok && propia.token) { setToken(propia.token); return currentClaims(); }
  if (SUITE_AUTH_URL) {
    try {
      const res = await fetch(`${SUITE_AUTH_URL}/auth/google`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ credential }),
      });
      if (res.ok) {
        const j = await res.json();
        const token = j.access_token || j.access || j.jwt;
        if (token) { setToken(token); return currentClaims(); }
      }
    } catch { /* sin backend → sesión de cliente */ }
  }
  // Sin api/: sesión de cliente con la cuenta de Google (habilita cotizar).
  setToken(mintToken({ role: ROLES.USUARIO, email, name: g?.name || email, google: true }));
  return currentClaims();
}

/**
 * Registro BÁSICO para cotizar (interino, pre-`api/`): abre una sesión de rol `client` con el
 * correo dado para habilitar la cotización básica con identidad. NO es premium ni verifica
 * credenciales (eso llega con `api/`).
 *
 * Ya NO acumula el lead en `sh:leads` (ver `purgarResiduosLocales`): esa lista no la leía nadie, y
 * el lead sí llega al servidor por el camino que importa — al cotizar, `save-quote` hace el upsert
 * en `users` con nombre y correo. La identidad de ESTA sesión viaja en el token, que sí se lee.
 */
const CUENTAS_KEY = 'sh:cuentas';

async function hashClave(s) {
  const t = `sh.v1:${s}`;
  if (globalThis.crypto?.subtle) {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(t));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  let h = 2166136261;
  for (let i = 0; i < t.length; i++) h = Math.imul(h ^ t.charCodeAt(i), 16777619);
  return `x${(h >>> 0).toString(16)}`;
}

function leerCuentas() {
  try { return JSON.parse(localStorage.getItem(CUENTAS_KEY) || '{}'); } catch { return {}; }
}

async function entrarCuentaLocal(email, password) {
  const e = String(email || '').trim().toLowerCase();
  const row = leerCuentas()[e];
  if (!row?.hash) return null;
  const h = await hashClave(password);
  if (h !== row.hash) return null;
  return {
    email: e,
    name: row.name || e,
    role: row.role,
    perfil: row.perfil,
    installerKind: row.installerKind,
    empresa: row.empresa,
  };
}

export async function registrarBasico({ nombre, email, password, perfil, installerKind, empresa } = {}) {
  const mail = String(email || '').trim().toLowerCase();
  if (!mail || !mail.includes('@')) throw new Error('Ingresa un correo válido.');
  const clave = String(password || '');
  if (clave.length < 8) throw new Error('La contraseña debe tener al menos 8 caracteres.');
  const name = String(nombre || '').trim() || mail;
  const p = Object.values(PERFILES_ALTA).includes(perfil) ? perfil : PERFILES_ALTA.CLIENTE;
  const kind = p === PERFILES_ALTA.INSTALADOR
    ? (Object.values(TIPOS_INSTALADOR).includes(installerKind) ? installerKind : TIPOS_INSTALADOR.TECNICO)
    : undefined;
  const razon = String(empresa || '').trim();
  if (kind === TIPOS_INSTALADOR.EMPRESA && razon.length < 2) {
    throw new Error('La empresa instaladora necesita razón social.');
  }
  const role = rolDesdePerfil(p);
  try {
    const all = leerCuentas();
    all[mail] = { hash: await hashClave(clave), name, role, perfil: p, installerKind: kind, empresa: razon || undefined };
    localStorage.setItem(CUENTAS_KEY, JSON.stringify(all));
  } catch { /* storage lleno: igual abre sesión */ }
  setToken(mintToken({
    role, email: mail, name, basico: true, perfil: p,
    installer_type: kind, empresa: razon || undefined,
  }));
  return currentClaims();
}
